import { useState } from 'react';
import type { ReactNode } from 'react';
import type { TenantConfig } from '@/types';
import { BrandMark } from './BrandMark';
import { SecurePill } from './SecurePill';

interface AppBarProps {
  tenant: TenantConfig;
  /** Sub-line under the tenant name, e.g. "Video-KYC", "Step 2 · Capture". */
  subtitle?: string;
  /** Right-hand slot — defaults to the Secure pill. */
  right?: ReactNode;
}

/**
 * Compact tenant lockup + trust pill. The logo placement is preserved.
 *
 * The tenant NAME renders as text whenever the mark still needs it (ruling,
 * review of owner round 2026-08-28 item 4): monogram fallback (no logo, or a
 * logo that failed to load) and SYMBOL-shaped logos — a square icon says
 * nothing without the name. A WORDMARK-shaped logo is the name and is not
 * repeated as text beside itself. BrandMark measures the loaded image and
 * reports which case applies.
 */
export function AppBar({ tenant, subtitle, right }: AppBarProps): JSX.Element {
  const [needsName, setNeedsName] = useState(() => !tenant.branding.logoUrl);
  return (
    <div className="appbar">
      <div className="lockup">
        <BrandMark tenant={tenant} onNeedsName={setNeedsName} />
        <div>
          {needsName ? (
            <div className="brand-name" style={{ fontSize: 'var(--fs-cap)' }}>
              {tenant.name}
            </div>
          ) : null}
          {subtitle ? <div className="brand-sub">{subtitle}</div> : null}
        </div>
      </div>
      {right ?? <SecurePill kind="secure" />}
    </div>
  );
}
